// Фото карточки (отель, место, тур, номер): снимок целиком, без обрезки — по краям размытая копия того же фото.
// Несколько фото: на телефоне — листать пальцем (прокрутка с привязкой к кадру), на компьютере — водить мышью
// по фото (каждая полоса ширины — свой кадр, как на Островке и Airbnb; увели мышь — снова первое фото).
// Грузятся только текущий и соседние кадры; фото, которое не загрузилось, выпадает из ленты.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { carouselZone, visibleSlides } from './photo-carousel-model';
import './photo-carousel.css';

export interface PhotoCarouselProps {
  images: string[];
  alt?: string;
  /** Класс контейнера: размеры задаёт карточка (hcard-photo, poi-photo…) */
  className?: string;
  /** Поверх фото: метки, «выбран», «Открыто сейчас» */
  children?: ReactNode;
  /** Нет ни одного рабочего фото — что показать (значок) */
  fallback?: ReactNode;
}

export function PhotoCarousel({ images, alt = '', className = '', children, fallback = null }: PhotoCarouselProps) {
  const [bad, setBad] = useState<string[]>([]);
  const [idx, setIdx] = useState(0);
  // Мышь над фото — грузим все кадры сразу, чтобы листание движением было мгновенным
  const [warm, setWarm] = useState(false);
  const track = useRef<HTMLDivElement>(null);
  const list = images.filter((u, i) => u && images.indexOf(u) === i && !bad.includes(u));
  const n = list.length;
  const key = images.join('|');
  useEffect(() => { setBad([]); setIdx(0); track.current?.scrollTo({ left: 0 }); }, [key]);

  const go = (i: number) => {
    const el = track.current;
    if (!el || i === idx) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'instant' as ScrollBehavior });
    setIdx(i);
  };

  if (!n) return <div className={`${className} noimg`}>{fallback}{children}</div>;

  const shown = warm ? new Set(list.map((_, i) => i)) : visibleSlides(idx, n);
  return (
    <div
      className={`${className} pc${n > 1 ? ' pc-many' : ''}`}
      data-photos={n}
      onMouseMove={n > 1 ? (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        go(carouselZone(e.clientX - r.left, r.width, n));
      } : undefined}
      onMouseEnter={n > 1 ? () => setWarm(true) : undefined}
      onMouseLeave={n > 1 ? () => go(0) : undefined}
    >
      <div className="pc-track" ref={track} onScroll={(e) => {
        const el = e.currentTarget;
        const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
        if (i !== idx && i >= 0 && i < n) setIdx(i);
      }}>
        {list.map((src, i) => (
          <div className="pc-slide" key={src}>
            {shown.has(i) ? <>
              <img className="pc-bg" src={src} alt="" aria-hidden="true" loading="lazy" decoding="async" referrerPolicy="no-referrer" />
              <img className="pc-img" src={src} alt={i === 0 ? alt : ''} loading={i === 0 ? 'lazy' : 'eager'} decoding="async" referrerPolicy="no-referrer"
                onError={() => setBad((b) => (b.includes(src) ? b : [...b, src]))} />
            </> : null}
          </div>
        ))}
      </div>
      {n > 1 ? (
        <div className="pc-dots" aria-hidden="true">
          {list.map((src, i) => <i key={src} className={i === idx ? 'on' : ''} />)}
        </div>
      ) : null}
      {n > 1 ? <span className="pc-count" aria-label={`Фото ${idx + 1} из ${n}`}>{`${idx + 1}/${n}`}</span> : null}
      {children}
    </div>
  );
}
